// EventCalendarUI.tsx
import itLocale from "@fullcalendar/core/locales/it";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React from "react";

import { Event } from "0.Mine/3.Interfaces/Event";

export interface EventCalendarUIProps {
  events: Event[];
  styles: {
    container: object;
    calendar: object;
  };
  handleSelect: (selectionInfo: any) => void;
  handleEventClick: (clickInfo: any) => void;
  handleEventDidMount: (info: any) => void;
}

export const EventCalendarUI: React.FC<EventCalendarUIProps> = ({
  events,
  styles,
  handleSelect,
  handleEventClick,
  handleEventDidMount,
}) => {
  // Use MUI's useMediaQuery to determine screen size
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Validate and filter events to ensure all have valid dates
  const validEvents = events.filter(
    (event) =>
      event &&
      event.start instanceof Date &&
      !isNaN(event.start.getTime()) &&
      (!event.end || (event.end instanceof Date && !isNaN(event.end.getTime())))
  );

  // Log invalid events for debugging
  const invalidEvents = events.filter(
    (event) =>
      !event ||
      !(event.start instanceof Date) ||
      isNaN(event.start.getTime()) ||
      (event.end && (!(event.end instanceof Date) || isNaN(event.end.getTime())))
  );

  if (invalidEvents.length > 0) {
    console.warn("Found invalid events:", invalidEvents);
  }

  // Adjust headerToolbar based on screen size
  const headerToolbar = isMobile
    ? {
        left: "prev,next",
        center: "title",
        right: "dayGridMonth,timeGridDay",
      }
    : {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
      };

  // Set the appropriate view based on device
  const initialView = isMobile ? "timeGridDay" : "dayGridMonth";

  return (
    <Box sx={styles.container}>
      <Box className="calEventss" sx={styles.calendar}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={initialView}
          headerToolbar={headerToolbar}
          events={validEvents}
          selectable={true}
          locale={itLocale}
          select={handleSelect}
          eventClick={handleEventClick}
          eventDidMount={handleEventDidMount}
          height="auto"
          stickyHeaderDates={true}
          expandRows={true}
          dayMaxEventRows={isMobile ? 2 : isTablet ? 3 : 6}
          longPressDelay={0}
          eventLongPressDelay={300}
          selectLongPressDelay={500}
          unselectAuto={true}
          unselectCancel=".fc-event"
          eventInteractive={true}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            meridiem: false,
          }}
          windowResizeDelay={100}
          moreLinkClick="day"
          views={{
            dayGridMonth: {
              titleFormat: { year: "numeric", month: "short" },
              dayHeaderFormat: { weekday: isMobile ? "narrow" : "short" },
            },
            timeGridWeek: {
              titleFormat: { year: "numeric", month: "short" },
              dayHeaderFormat: { weekday: isMobile ? "narrow" : "short" },
            },
          }}
          dayHeaderContent={(args) => {
            // Custom formatting for day headers in timeGrid views
            if (args.view.type.includes("timeGrid")) {
              return (
                <>
                  <div className="fc-day-name">
                    {args.date.toLocaleDateString("it", { weekday: isMobile ? "narrow" : "short" })}
                  </div>
                  <div className="fc-day-number">{args.date.getDate()}</div>
                </>
              );
            }
            // Default formatting for other views
            return args.text;
          }}
        />
      </Box>
    </Box>
  );
};
